// CSV serialization for Manufacturing → Models export (round-trips the
// bulk-import column contract in recipeBulkImportExample.ts).
// Includes material, component (sub-recipe), and part-use lines.

import type { MfgRecipeDetailResponse, MfgRecipeSummary } from "@/lib/manufacturing/types";

export const RECIPE_EXPORT_HEADERS = [
  "recipe_code",
  "recipe_name",
  "recipe_kind",
  "recipe_tags",
  "recipe_notes",
  "line_kind",
  "line_material_name",
  "line_component_recipe_code",
  "line_material_use_code",
  "line_quantity",
  "line_role",
  "line_slot_label",
  "line_upgrade_price_delta",
] as const;

const NEEDS_QUOTING = /[",\n\r]/;

function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  if (!NEEDS_QUOTING.test(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

export type RecipeExportLineKind = "material" | "component" | "use";

export type RecipeExportLine = {
  line_kind: RecipeExportLineKind;
  material_name: string;
  component_recipe_code: string;
  material_use_code: string;
  quantity: number;
  role: string;
  slot_label: string;
  upgrade_price_delta: number | null;
};

export type RecipeExportDetail = {
  recipe: Pick<MfgRecipeSummary, "code" | "name" | "kind" | "tags" | "notes"> & {
    notes?: string;
  };
  /** Full BOM — material, sub-recipe (component), and part-use lines. */
  lines: RecipeExportLine[];
  /** @deprecated Prefer `lines`. Kept for PDF callers during migration. */
  materialLines: RecipeExportLine[];
};

/** Build export rows from recipe detail API payloads. */
export function recipeDetailsToExportRows(
  details: ReadonlyArray<MfgRecipeDetailResponse>,
): RecipeExportDetail[] {
  return details.map((d) => {
    const lines: RecipeExportLine[] = [];
    for (const l of d.lines) {
      if (l.line_kind === "material" && l.material_name) {
        lines.push({
          line_kind: "material",
          material_name: l.material_name,
          component_recipe_code: "",
          material_use_code: "",
          quantity: l.quantity,
          role: l.role,
          slot_label: l.slot_label,
          upgrade_price_delta: l.upgrade_price_delta,
        });
      } else if (l.line_kind === "component" && l.component_recipe_code) {
        lines.push({
          line_kind: "component",
          material_name: "",
          component_recipe_code: l.component_recipe_code,
          material_use_code: "",
          quantity: l.quantity,
          role: l.role,
          slot_label: l.slot_label,
          upgrade_price_delta: l.upgrade_price_delta,
        });
      } else if (l.line_kind === "use" && l.material_use_code) {
        lines.push({
          line_kind: "use",
          material_name: "",
          component_recipe_code: "",
          material_use_code: l.material_use_code,
          quantity: l.quantity,
          role: l.role,
          slot_label: l.slot_label,
          upgrade_price_delta: l.upgrade_price_delta,
        });
      }
    }
    return {
      recipe: {
        code: d.recipe.code,
        name: d.recipe.name,
        kind: d.recipe.kind,
        tags: d.recipe.tags,
        notes: d.recipe.notes,
      },
      lines,
      materialLines: lines,
    };
  });
}

/** Serialize recipes to the bulk-import CSV shape (one row per BOM line). */
export function recipesToCsv(exports: ReadonlyArray<RecipeExportDetail>): string {
  const lines = [RECIPE_EXPORT_HEADERS.join(",")];
  for (const item of exports) {
    const tags = Array.isArray(item.recipe.tags) ? item.recipe.tags.join(";") : "";
    const notes = item.recipe.notes ?? "";
    const bom = item.lines ?? item.materialLines ?? [];
    if (bom.length === 0) {
      lines.push(
        [
          item.recipe.code,
          item.recipe.name,
          item.recipe.kind,
          tags,
          notes,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
        ]
          .map(escapeCell)
          .join(","),
      );
      continue;
    }
    bom.forEach((line, idx) => {
      lines.push(
        [
          item.recipe.code,
          idx === 0 ? item.recipe.name : "",
          idx === 0 ? item.recipe.kind : "",
          idx === 0 ? tags : "",
          idx === 0 ? notes : "",
          line.line_kind,
          line.material_name,
          line.component_recipe_code,
          line.material_use_code,
          line.quantity,
          line.role,
          line.slot_label,
          line.upgrade_price_delta ?? "",
        ]
          .map(escapeCell)
          .join(","),
      );
    });
  }
  return lines.join("\n") + "\n";
}
